import { useState, useMemo } from 'react'
import { Calendar, Clock, ChevronLeft, ChevronRight, X, CalendarPlus, Check, Loader2, ListChecks, CheckSquare, Timer } from 'lucide-react'
import { useCardHistory } from '../hooks/useCardHistory'

const FIELD_LABELS = {
    title: 'Titlu', description: 'Descriere', list_id: 'Lista',
    is_completed: 'Completat', priority: 'Prioritate',
    status: 'Status', due_date: 'Data scadenta',
    created: 'Creat', current_state: 'Stare curenta',
    checklist_completed: 'Checklist completat',
    completion_payload: 'Payload completare',
}

const EVENT_ICONS = {
    snapshot: Calendar,
    checklist_item: ListChecks,
    card_done: CheckSquare,
    time_log: Timer,
    card_recurring: Clock,
}

const EVENT_COLORS = {
    snapshot: 'var(--accent-purple)',
    checklist_item: '#60a5fa',
    card_done: '#22c55e',
    time_log: '#a78bfa',
    card_recurring: '#f59e0b',
}

function formatTime(dateStr) {
    const d = new Date(dateStr)
    return d.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })
}

function formatDate(dateStr) {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('ro-RO', {
        weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    })
}

function formatDuration(minutes) {
    if (!minutes || minutes <= 0) return null
    if (minutes < 60) return `${minutes}m`
    return `${Math.floor(minutes / 60)}h ${minutes % 60 > 0 ? `${minutes % 60}m` : ''}`
}

function getEventLabel(entry) {
    switch (entry.type) {
        case 'checklist_item': return 'A completat un pas'
        case 'card_done': return 'Card marcat gata'
        case 'time_log': return 'Sesiune timer'
        case 'card_recurring': return 'Task repetitiv completat'
        case 'snapshot': {
            const fields = entry.changed_fields || []
            if (fields.includes('created')) return 'Card creat'
            if (fields.includes('current_state')) return 'Stare salvata'
            if (fields.includes('checklist_completed')) return 'Checklist completat'
            // Prefer the rich human-readable line ("Creat de X",
            // "Comentariu: …", "Mutat în «…»") as the headline instead of
            // the generic "Modificare".
            return entry.detail_text || fields.map(f => FIELD_LABELS[f] || f).join(', ') || 'Modificare'
        }
        default: return 'Eveniment'
    }
}

export default function CardDateHistory({ cardId, userId, isAdmin }) {
    const {
        timeline,
        scheduledChanges,
        availableDates,
        loading,
        getEntriesForDate,
        scheduleChange,
        cancelScheduledChange,
    } = useCardHistory(cardId)

    const [selectedDate, setSelectedDate] = useState(null)
    const [showScheduleForm, setShowScheduleForm] = useState(false)
    const [scheduleDate, setScheduleDate] = useState('')
    const [scheduleField, setScheduleField] = useState('title')
    const [scheduleValue, setScheduleValue] = useState('')
    const [scheduling, setScheduling] = useState(false)

    const activeDate = selectedDate || availableDates[0] || null
    const dateEntries = useMemo(() => {
        if (!activeDate) return []
        return getEntriesForDate(activeDate)
    }, [activeDate, getEntriesForDate])

    const dateIndex = availableDates.indexOf(activeDate)
    const canGoBack = dateIndex < availableDates.length - 1
    const canGoForward = dateIndex > 0

    const handleSchedule = async () => {
        if (!scheduleDate || !scheduleField || !scheduleValue) return
        setScheduling(true)
        await scheduleChange(scheduleDate, { [scheduleField]: scheduleValue }, userId)
        setScheduling(false)
        setShowScheduleForm(false)
        setScheduleDate('')
        setScheduleValue('')
    }

    const tomorrow = useMemo(() => {
        const d = new Date()
        d.setDate(d.getDate() + 1)
        return d.toISOString().split('T')[0]
    }, [])

    const pendingChanges = scheduledChanges.filter((c) => c.status === 'pending')

    if (loading && timeline.length === 0) {
        return <div className="cdh-loading"><Loader2 size={18} className="gsearch-spinner" /> Se incarca istoricul...</div>
    }

    return (
        <div className="cdh-container">
            {/* Date navigator */}
            <div className="cdh-nav">
                <button className="cdh-nav-btn" disabled={!canGoBack}
                    onClick={() => setSelectedDate(availableDates[dateIndex + 1])} title="Ziua anterioara">
                    <ChevronLeft size={16} />
                </button>
                <div className="cdh-nav-date">
                    <Calendar size={14} />
                    {activeDate ? formatDate(activeDate) : 'Niciun istoric'}
                </div>
                <button className="cdh-nav-btn" disabled={!canGoForward}
                    onClick={() => setSelectedDate(availableDates[dateIndex - 1])} title="Ziua urmatoare">
                    <ChevronRight size={16} />
                </button>
            </div>

            {/* Summary for the day */}
            {activeDate && dateEntries.length > 0 && (
                <div className="cdh-day-summary">
                    {(() => {
                        const checklistCount = dateEntries.filter(e => e.type === 'checklist_item').length
                        const snapshotCount = dateEntries.filter(e => e.type === 'snapshot').length
                        const cardDone = dateEntries.some(e => e.type === 'card_done')
                        const parts = []
                        if (checklistCount > 0) parts.push(`${checklistCount} pasi completati`)
                        if (snapshotCount > 0) parts.push(`${snapshotCount} modificari`)
                        if (cardDone) parts.push('Card marcat gata')
                        return parts.join(' · ')
                    })()}
                </div>
            )}

            {/* Timeline */}
            {activeDate && dateEntries.length > 0 ? (
                <div className="cdh-timeline">
                    {dateEntries.map((entry) => {
                        const Icon = EVENT_ICONS[entry.type] || Calendar
                        const color = EVENT_COLORS[entry.type] || 'var(--text-secondary)'
                        return (
                            <div key={entry.id} className="cdh-entry">
                                <div className="cdh-entry-icon" style={{ color }}>
                                    <Icon size={14} />
                                </div>
                                <div className="cdh-entry-content">
                                    <div className="cdh-entry-header">
                                        <span className="cdh-entry-label">{getEventLabel(entry)}</span>
                                        <span className="cdh-entry-time">{formatTime(entry.timestamp)}</span>
                                    </div>
                                    {entry.detail_text && entry.detail_text !== getEventLabel(entry) && (
                                        <div className="cdh-entry-detail">{entry.detail_text}</div>
                                    )}
                                    {entry.duration_minutes > 0 && (
                                        <div className="cdh-entry-detail">{formatDuration(entry.duration_minutes)}</div>
                                    )}
                                    {entry.user_name && (
                                        <span className="cdh-entry-author">{entry.user_name}</span>
                                    )}
                                </div>
                            </div>
                        )
                    })}
                </div>
            ) : activeDate ? (
                <div className="cdh-empty">Nicio activitate in aceasta zi.</div>
            ) : null}

            {/* Scheduled changes */}
            {pendingChanges.length > 0 && (
                <div className="cdh-scheduled">
                    <div className="cdh-scheduled-title">
                        <CalendarPlus size={14} /> Modificari programate
                    </div>
                    {pendingChanges.map((change) => (
                        <div key={change.id} className="cdh-scheduled-item">
                            <div className="cdh-scheduled-date">{formatDate(change.effective_date)}</div>
                            <div className="cdh-scheduled-fields">
                                {Object.entries(change.changes || {}).map(([key, val]) => (
                                    <span key={key} className="cdh-scheduled-field">
                                        {FIELD_LABELS[key] || key}: <strong>{String(val)}</strong>
                                    </span>
                                ))}
                            </div>
                            {isAdmin && (
                                <button className="cdh-cancel-btn" onClick={() => cancelScheduledChange(change.id)} title="Anuleaza">
                                    <X size={12} />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Schedule form (admin) */}
            {isAdmin && (
                <div className="cdh-schedule-section">
                    {!showScheduleForm ? (
                        <button className="cdh-schedule-btn" onClick={() => setShowScheduleForm(true)}>
                            <CalendarPlus size={14} /> Programeaza modificare
                        </button>
                    ) : (
                        <div className="cdh-schedule-form">
                            <div className="cdh-schedule-form-title">Modificare programata</div>
                            <label className="cdh-label">
                                Data aplicare
                                <input type="date" className="cdh-date-input" value={scheduleDate}
                                    onChange={(e) => setScheduleDate(e.target.value)} min={tomorrow} />
                            </label>
                            <label className="cdh-label">
                                Camp
                                <select className="cdh-select" value={scheduleField}
                                    onChange={(e) => setScheduleField(e.target.value)}>
                                    <option value="title">Titlu</option>
                                    <option value="description">Descriere</option>
                                    <option value="priority">Prioritate</option>
                                    <option value="status">Status</option>
                                </select>
                            </label>
                            <label className="cdh-label">
                                Valoare noua
                                <input type="text" className="cdh-text-input" value={scheduleValue}
                                    onChange={(e) => setScheduleValue(e.target.value)} placeholder="Introdu valoarea..." />
                            </label>
                            <div className="cdh-schedule-actions">
                                <button className="cdh-confirm-btn" onClick={handleSchedule}
                                    disabled={scheduling || !scheduleDate || !scheduleValue}>
                                    {scheduling ? <Loader2 size={14} className="gsearch-spinner" /> : <Check size={14} />}
                                    Programeaza
                                </button>
                                <button className="cdh-cancel-form-btn" onClick={() => setShowScheduleForm(false)}>Anuleaza</button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
